// Страницы документации «Интеграции внешних систем» (main IntegrationController, заказы — сервис каталога).
// Авторизация и общие ошибки описаны один раз — на странице «Просмотр параметров авторизации».
import type { ApiDocBlock, ApiDocPage } from '../types'

const seeAuthPage
  = 'Авторизация (заголовок uuid и Basic) и общие ошибки (401, 502, форматы 422) — на странице «Просмотр параметров авторизации».'

const paramsResponse = (login: string | null, password: string) => `{
  "success": 1,
  "data": {
    "headers": {
      "uuid": "0eabf35d-a2a3-4c9a-b1a8-9691373902f3"
    },
    "basic_auth": {
      "login": ${login === null ? 'null' : `"${login}"`},
      "password": "${password}"
    }
  },
  "error": null
}`

const customerIdResponse = `{
  "success": 1,
  "data": {
    "id": 2  // id покупателя в нашей системе
  },
  "error": null
}`

const orderParams = `{
  "user_id": 2,              // обязательно, id покупателя (из ответа POST /api/integration/v1/customers)
  "payment_system_id": 1,    // обязательно, id активной платёжной системы компании
  "bonus": 0,                // не обязательно, сколько оплачено бонусами, не меньше 0; по умолчанию 0
  "comment": "Без лука",     // не обязательно, комментарий к заказу
  "products": [              // обязательно, если нет kits
    {
      "id": 82,              // обязательно, id активного торгового предложения
      "quantity": 2          // обязательно, целое, не меньше 1
    }
  ],
  "kits": [                  // обязательно, если нет products
    {
      "id": 3,               // обязательно, id набора
      "quantity": 1,         // обязательно, целое, не меньше 1
      "products": [10, 12]   // обязательно, id торговых предложений — по одному на каждую позицию набора, в порядке позиций
    }
  ],
  "delivery": {              // обязательно
    "id": 2,                 // id активного способа доставки; если не передан — подставляется самовывоз
    "store": 5,              // обязательно для самовывоза: id магазина, где разрешён самовывоз
    "date": "2026-12-15",    // не обязательно, дата не раньше сегодняшней
    "interval": "10:00-12:00", // обязательно для доставки курьером с интервалами: интервал из настроек или его id
    "address": "ул. Примерная, д. 10", // обязательно для доставки курьером с интервалами
    "apartment": "42",       // не обязательно
    "entrance": "3",         // не обязательно
    "intercom": "142",       // не обязательно
    "cost": 300,             // не обязательно, стоимость доставки; если не передана — считается по настройкам доставки
    "recipient_name": "Иванов Иван Иванович", // обязательно
    "recipient_phone": "+79001234567",        // обязательно
    "recipient_email": "example@mail.com",    // не обязательно
    "recipient_note": "Позвонить за час до доставки", // не обязательно
    "courier_name": "Пётр",  // не обязательно
    "courier_phone": "+79007654321", // не обязательно
    "anonymously": false     // не обязательно, true или false
  }
}`

const orderResponse = `{
  "success": 1,
  "data": {
    "id": 1,
    "ext_id": null,
    "user_id": 2,
    "user_name": "Иванов Иван Иванович",
    "total": 2182,             // сумма заказа вместе с доставкой
    "paid_with_bonus": 0,      // значение bonus из запроса
    "amount_to_pay": 2182,     // total минус paid_with_bonus
    "paid_amount": 0,          // сколько уже оплачено
    "status": {
      "id": 1,
      "ext_id": null,
      "code": "created",
      "name": "CREATED",       // название статуса в настройках компании
      "active": 1,
      "is_initial": true,
      "is_final": false,
      "is_success": false,
      "is_canceled": false
    },
    "paid": false,
    "canceled": false,
    "created_at": "13:53 04.07.2026",
    "created_at_timestamp": 1783162395
  },
  "error": null
}`

const orderMainError = `{
  "success": 0,
  "error": "Значение поля Пользователь не существует.",
  "data": {
    "user_id": ["Значение поля Пользователь не существует."]
  }
}`

const orderCatalogError = `{
  "success": 0,
  "data": null,
  "error": {
    "message": "Выбранное значение для products.0.id некорректно.",
    "errors": {
      "products.0.id": ["Выбранное значение для products.0.id некорректно."]
    }
  }
}`

const orderErrors422: ApiDocBlock = {
  id: 'errors422',
  heading: 'Ошибки проверки (422)',
  text: [
    'Ошибки приходят в двух форматах. user_id и payment_system_id проверяются первыми: при ошибке в error — строка, в data — ошибки по полям.',
    'Остальные поля проверяет сервис заказов: в data — null, в error — объект с message (первая ошибка) и errors (все ошибки по полям, например products.0.id).',
  ],
  code: `${orderMainError}

${orderCatalogError}`,
}

export const integrationApiDocs: Record<string, ApiDocPage> = {
  integrationAuth: {
    link: ['GET /api/dashboard/v1/chief/integration/params'],
    blocks: [
      {
        id: 'auth',
        heading: 'Авторизация в API интеграций',
        nav: 'Авторизация',
        text: [
          'Все методы /api/integration/v1 вызываются с двумя заголовками: uuid — uuid компании (headers.uuid из ответа этого метода) и Authorization: Basic — логин и пароль из basic_auth, записанные как «логин:пароль» в base64.',
          'Логин и пароль задаёт руководитель (страница «Настройка логина и пароля»). Пока они не заданы, все методы /api/integration/v1 отвечают 401.',
          'Пароль в ответе не показывается: только «Установлен» или «Не установлен».',
        ],
        code: `uuid: 0eabf35d-a2a3-4c9a-b1a8-9691373902f3
Authorization: Basic dGVzdDpzZWNyZXQ=   // base64 от "test:secret"`,
      },
      {
        id: 'common-errors',
        heading: 'Общие ошибки методов интеграции',
        nav: 'Общие ошибки',
        text: [
          '401 «Unauthenticated» — нет заголовка uuid, компания с таким uuid не найдена, логин и пароль не заданы или не совпадают.',
          '404 «Не найдено» — нет покупателя с указанным id.',
          '502 «Ошибка соединения с внешним сервисом» — не удалось связаться с сервисом заказов или бонусов.',
          '504 — сервис не ответил вовремя на запись (заказ, начисление или списание бонусов). Такие запросы не повторяются автоматически: операция могла выполниться, поэтому сначала проверьте результат, потом повторяйте.',
          '422 — ошибка в данных запроса: в error — первая ошибка строкой, в data — все ошибки по полям. Методы заказов могут вернуть и второй формат — см. «Импорт заказа».',
        ],
        code: `{
  "success": 0,
  "data": null,
  "error": "Unauthenticated"
}

{
  "success": 0,
  "error": "Поле Телефон обязательно.",
  "data": {
    "phone": ["Поле Телефон обязательно."]
  }
}`,
      },
      {
        id: 'method',
        heading: 'Как получить параметры',
        text: [
          'Метод кабинета: вызывается руководителем компании с токеном входа в кабинет (Authorization: Bearer).',
        ],
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        code: paramsResponse('test', 'Установлен'),
      },
      {
        id: 'not-set',
        heading: 'Если логин и пароль не заданы',
        nav: 'Без логина и пароля',
        text: ['API интеграций в этом случае закрыт: все его методы отвечают 401.'],
        code: paramsResponse(null, 'Не установлен'),
      },
    ],
  },

  integrationAuthSettings: {
    link: ['PATCH /api/dashboard/v1/chief/integration/params'],
    blocks: [
      {
        id: 'params',
        heading: 'Параметры',
        text: [
          'Метод кабинета: вызывается руководителем с токеном входа в кабинет (Authorization: Bearer).',
          'Логин и пароль передаются только вместе. Новые значения заменяют старые.',
          'Запрос без login и password (или с null в обоих) стирает логин и пароль — после этого все методы /api/integration/v1 отвечают 401.',
        ],
        code: `{
  "login": "test",     // строка; обязательно, если передан password
  "password": "secret" // строка; обязательно, если передан login
}`,
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        text: ['Ответ — как у «Просмотр параметров авторизации».'],
        code: paramsResponse('test', 'Установлен'),
      },
      {
        id: 'errors',
        heading: 'Ошибка проверки (422)',
        code: `{
  "success": 0,
  "error": "Поле Пароль обязательно для заполнения, когда поле Логин указано.",
  "data": {
    "password": ["Поле Пароль обязательно для заполнения, когда поле Логин указано."]
  }
}`,
      },
    ],
  },

  integrationClientImport: {
    link: ['POST /api/integration/v1/customers'],
    blocks: [
      {
        id: 'params',
        heading: 'Параметры',
        text: [
          'Какое поле обязательно, зависит от способа входа в приложение, заданного в настройках компании: по телефону, по почте или по телефону и почте.',
          'Телефон — российский номер в любом виде: +7 (900) 123-45-67, 89001234567, 9001234567. Сохраняется как 79001234567.',
        ],
        code: `{
  "phone": "+7 900 123-45-67",    // обязательно при входе по телефону или по телефону и почте
  "email": "customer@email.com",  // обязательно при входе по почте или по телефону и почте; адрес почты
  "name": "Иванов Иван Иванович", // не обязательно, строка
  "birthday": "2000-01-01"        // не обязательно, дата
}`,
      },
      {
        id: 'lookup',
        heading: 'Создание или обновление',
        text: [
          'Метод создаёт покупателя или обновляет найденного. Поиск идёт по полю входа: при входе по телефону — по phone, при входе по почте — по email.',
          'При входе по телефону и почте покупатель ищется по паре phone и email сразу. Если такой пары нет, создаётся новый покупатель, даже когда телефон или почта уже есть у другого.',
          'У найденного покупателя обновляются переданные поля. Поле, переданное как null, очищается; не переданное не меняется.',
        ],
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        code: customerIdResponse,
      },
      {
        id: 'errors',
        heading: 'Ошибка проверки (422)',
        text: [seeAuthPage],
        code: `{
  "success": 0,
  "error": "Поле Телефон обязательно.",
  "data": {
    "phone": ["Поле Телефон обязательно."]
  }
}`,
      },
    ],
  },

  integrationClientEdit: {
    link: ['PATCH /api/integration/v1/customers/:id'],
    blocks: [
      {
        id: 'params',
        heading: 'Параметры',
        text: [
          ':id — id покупателя в нашей системе.',
          'Передаются только меняющиеся поля, остальные не меняются.',
          'Поле входа (телефон или почта — по способу входа в настройках компании) можно не передавать, но нельзя передать пустым: по нему покупатель входит в приложение. Оно должно быть уникальным среди покупателей компании.',
          'name, birthday и второе контактное поле (не поле входа), переданные как null, очищаются.',
          'Телефон принимается в любом виде и сохраняется как 79001234567.',
        ],
        code: `{
  "phone": "+7 900 123-45-67",    // не обязательно; если передано и это поле входа — не пустое и не занятое
  "email": "customer@email.com",  // не обязательно; если передано и это поле входа — не пустое и не занятое
  "name": "Иванов Иван Иванович", // не обязательно, строка или null
  "birthday": "2000-01-01"        // не обязательно, дата или null
}`,
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        code: customerIdResponse,
      },
      {
        id: 'errors',
        heading: 'Ошибки',
        text: [
          '404 «Не найдено» — покупателя с таким id нет.',
          '422 — телефон или почта уже заняты другим покупателем, поле входа передано пустым или в неверном формате.',
          seeAuthPage,
        ],
        code: `{
  "success": 0,
  "error": "Такое значение поля Телефон уже существует.",
  "data": {
    "phone": ["Такое значение поля Телефон уже существует."]
  }
}`,
      },
    ],
  },

  integrationCustomerPush: {
    link: ['POST /api/integration/v1/customers/push'],
    blocks: [
      {
        id: 'params',
        heading: 'Параметры',
        text: [
          'Отправляет покупателю push-уведомление в мобильное приложение.',
          'Покупатель указывается хотя бы одним из полей id, ext_id, email, phone. Если передано несколько, покупатель должен совпасть по всем.',
          'Телефон принимается в любом виде и приводится к 79001234567.',
        ],
        code: `{
  "title": "Заказ готов",           // обязательно, строка до 255 символов
  "body": "Заберите заказ в магазине", // обязательно, строка
  "id": 2,                          // id покупателя в нашей системе, целое
  "ext_id": "crm-77",               // id покупателя во внешней системе, строка
  "email": "customer@email.com",    // почта покупателя
  "phone": "79001234567"            // телефон покупателя
}`,
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        text: [
          'В data — id найденного покупателя. Уведомление ставится в очередь и уходит на устройства, где покупатель вошёл в приложение. Если таких устройств нет, ответ тот же, но уведомление никуда не придёт.',
          'В данных уведомления type всегда равен custom.message.',
        ],
        code: customerIdResponse,
      },
      {
        id: 'errors',
        heading: 'Ошибки',
        text: [
          '404 «Не найдено» — покупатель по переданным полям не найден.',
          '422 «Найдено несколько клиентов» — под переданные поля подходит больше одного покупателя; уточните запрос, например добавьте id.',
          '422 — нет title или body, не передано ни одно поле покупателя, поле в неверном формате.',
          seeAuthPage,
        ],
        code: `{
  "success": 0,
  "error": "Найдено несколько клиентов",
  "data": {
    "ext_id": ["Найдено несколько клиентов"]
  }
}`,
      },
    ],
  },

  integrationOrderImport: {
    link: ['POST /api/integration/v1/orders'],
    blocks: [
      {
        id: 'params',
        heading: 'Параметры',
        text: [
          'Нужен хотя бы один из списков: products или kits.',
          'Имя, телефон и почта покупателя в заказе берутся из карточки покупателя user_id.',
          'Для самовывоза обязателен delivery.store. Для доставки курьером с интервалами обязательны delivery.interval и delivery.address.',
        ],
        code: orderParams,
      },
      {
        id: 'bonus',
        heading: 'Оплата бонусами',
        text: [
          'bonus только записывается в заказ как оплаченное бонусами (paid_with_bonus) и уменьшает amount_to_pay. При импорте бонусы не списываются и баланс покупателя не проверяется.',
          'Списание происходит, когда заказ переходит в статус «выполнен» (code done), если у компании включена бонусная программа. Если бонусов к этому моменту не хватит, они не спишутся, а заказ останется как есть.',
          'Если компания подключила внешнюю бонусную систему, бонусы заказа, созданного через API, не списываются.',
        ],
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        code: orderResponse,
      },
      {
        id: 'errors400',
        heading: 'Ошибки (400)',
        text: [
          '«Доставка в указанную дату невозможна» — на эту дату доставка закрыта в настройках.',
          '«Доставка в указанное время невозможна» — на эту дату закрыт выбранный интервал.',
          '«Бонусы не могут быть отрицательными» — bonus меньше 0.',
        ],
        code: `{
  "success": 0,
  "data": null,
  "error": "Доставка в указанную дату невозможна"
}`,
      },
      orderErrors422,
      {
        id: 'no-answer',
        heading: 'Если ответ не пришёл',
        text: [
          'Ответ 504 «Сервис заказов не ответил» значит, что заказ мог создаться. Запрос не повторяется автоматически, чтобы не создать дубль: проверьте заказы покупателя в кабинете, прежде чем повторять.',
          seeAuthPage,
        ],
      },
    ],
  },

  integrationOrderEdit: {
    link: ['PATCH /api/integration/v1/orders/:id'],
    blocks: [
      {
        id: 'params',
        heading: 'Параметры',
        text: [
          ':id — id заказа из ответа импорта.',
          'Заказ перезаписывается целиком: передаётся полный состав, как при импорте, с теми же обязательными полями. Позиции заказа заменяются присланными, доставка — присланной.',
          'bonus, если его не передать, обнуляется. comment, если его не передать, остаётся прежним. Статус и оплата заказа не меняются.',
          'Имя, телефон и почта покупателя обновляются из карточки покупателя user_id.',
          'Письмо руководителю о новом заказе при изменении не отправляется — только при импорте.',
        ],
        code: orderParams,
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        text: ['Ответ — заказ после изменения, как у «Импорт заказа».'],
        code: orderResponse,
      },
      {
        id: 'errors',
        heading: 'Ошибки',
        text: [
          '404 — заказа с таким id нет.',
          '400 «Доставка в указанную дату невозможна», «Доставка в указанное время невозможна» и «Бонусы не могут быть отрицательными» — как при импорте.',
          '422 — в тех же двух форматах, что при импорте.',
          '504 «Сервис заказов не ответил» — изменение могло примениться: проверьте заказ в кабинете, прежде чем повторять.',
          seeAuthPage,
        ],
        code: orderCatalogError,
      },
    ],
  },
}
